import { useParams } from 'react-router';
import { When } from 'react-if';

import { InlineDateInput } from '../../../../common/Atoms/InlineDateInput';
import { IInlineSelectOption, InlineSelect } from '../../../../common/Atoms/InlineSelect';
import { env } from '../../../../common/lib/env';
import { useAccountingSystemName } from '../../../hooks/useAccountingSystemName';
import { useCurrentOrgUsers } from '../../../hooks/useCurrentOrgUsers';
import { useInvoice } from '../../../hooks/useInvoice';
import { useAssignInvoiceMutation, useSetExpectedPaymentDateMutation } from '../../../services/api/invoiceApi/invoice';
import { InvoiceQuery } from '../InvoiceDocument/InvoiceQuery';
import { InvoiceTagSelector } from '../InvoiceDocument/InvoiceTagSelector';
import { PaymentPromise } from '../InvoiceDocument/PaymentPromise';

import { DetailField } from './DetailField';

const linkClass = `text-paidniceGreen-600 hover:text-paidniceGreen-700 hover:underline`;

export function InvoiceDetailsCard() {
  const { organisationId, invoiceId } = useParams();
  const { invoice } = useInvoice();
  const accountingSystemName = useAccountingSystemName();
  const [setExpectedDate, { isLoading: expectedDateLoading }] = useSetExpectedPaymentDateMutation();
  const [assignInvoice, { isLoading: assignLoading }] = useAssignInvoiceMutation();
  const orgUsers = useCurrentOrgUsers(true);

  if (!invoice) return null;

  const unpaid = invoice.xero_status === `AUTHORISED`;
  const ownerOptions: IInlineSelectOption<string | null>[] = [
    { label: `Unassigned`, value: null },
    ...orgUsers,
  ];

  if (invoice.assigned_user_id && !orgUsers.some(u => u.value === invoice.assigned_user_id)) {
    ownerOptions.push({ label: `Removed user`, value: invoice.assigned_user_id, disabled: true });
  }

  async function onExpectedDateSave(date: string) {
    await setExpectedDate({ date, invoiceId, organisationId }).unwrap();
  }

  return (
    <div className={ `grid shrink-0 grid-cols-2 gap-x-5 gap-y-2.5 rounded-xl border border-gray-200 bg-white px-4 py-3` }>
      <DetailField label={ `Owner` }>
        <InlineSelect
          options={ ownerOptions }
          value={ invoice.assigned_user_id || null }
          onChange={ userId => assignInvoice({ organisationId, invoiceId, userId }) }
          disabled={ assignLoading }
        />
      </DetailField>

      <DetailField label={ `Expected payment` }>
        <InlineDateInput
          label={ `Expected payment date` }
          value={ invoice.expected_payment_date_str }
          onSave={ onExpectedDateSave }
          disabled={ expectedDateLoading }
        />
      </DetailField>

      <PaymentPromise
        compact
        amountDueCents={ invoice.xero_amount_due ?? 0 }
        currency={ invoice.xero_currency }
        unpaid={ unpaid }
      />
      <InvoiceQuery
        compact
        unpaid={ unpaid }
      />

      <DetailField label={ `Tags` }>
        <InvoiceTagSelector inline />
      </DetailField>

      <DetailField label={ `Open in` }>
        <span className={ `flex gap-2.5` }>
          <When condition={ !!invoice.externalLink }>
            <a
              href={ invoice.externalLink }
              target={ `_blank` }
              rel={ `noopener noreferrer` }
              className={ linkClass }
            >
              { `${accountingSystemName} ↗` }
            </a>
          </When>
          <a
            href={ `${env.portalWebUrl}/i/${invoice.hash}` }
            target={ `_blank` }
            rel={ `noopener noreferrer` }
            className={ linkClass }
          >
            { `Portal ↗` }
          </a>
        </span>
      </DetailField>
    </div>
  );
}
